/**
 * Schema.org JSON-LD Generators for SEO
 * Standardized schemas for Google Search, Rich Snippets, Voice Search & AI Assistants
 */

// 1. PostalAddress
export function createPostalAddress({
  streetAddress = "Sohna Road, Sector 48",
  addressLocality = "Gurgaon",
  addressRegion = "Haryana",
  postalCode = "122018",
  addressCountry = "IN"
} = {}) {
  return {
    "@type": "PostalAddress",
    streetAddress,
    addressLocality,
    addressRegion,
    postalCode,
    addressCountry
  };
}

// 2. GeoCoordinates
export function createGeoCoordinates({
  latitude = 28.4231,
  longitude = 77.0423
} = {}) {
  return {
    "@type": "GeoCoordinates",
    latitude,
    longitude
  };
}

// 3. Review & AggregateRating
export function createReview({
  authorName,
  ratingValue = 5,
  reviewBody,
  datePublished = "2026-02-10"
}) {
  return {
    "@type": "Review",
    author: {
      "@type": "Person",
      name: authorName
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue,
      bestRating: 5,
      worstRating: 1
    },
    reviewBody,
    datePublished
  };
}

export function createAggregateRating({
  ratingValue = "4.9",
  reviewCount = "1280",
  bestRating = "5",
  worstRating = "1"
} = {}) {
  return {
    "@type": "AggregateRating",
    ratingValue,
    reviewCount,
    bestRating,
    worstRating
  };
}

// 4. ImageObject
export function createImageObject({
  url = "https://print.inchpaper.com/inchpaper-logo.png",
  caption = "Inchpaper Self-Service Printing Kiosk",
  width = 1200,
  height = 630
} = {}) {
  return {
    "@type": "ImageObject",
    url,
    caption,
    width,
    height
  };
}

// 5. VideoObject
export function createVideoObject({
  name = "How to Print Documents Instantly at Inchpaper Kiosks",
  description = "Learn how to upload your PDF online, select print settings, scan QR code at any Inchpaper self-service kiosk, and receive instant prints.",
  thumbnailUrl = "https://print.inchpaper.com/favicon-512.png",
  uploadDate = "2026-01-01T08:00:00+05:30",
  contentUrl = "https://print.inchpaper.com/demo-video.mp4",
  embedUrl = "https://print.inchpaper.com/#how-it-works"
} = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    description,
    thumbnailUrl,
    uploadDate,
    contentUrl,
    embedUrl
  };
}

// 6. Speakable
export function createSpeakable(cssSelectors = ["h1", ".speakable-text", "p.intro"]) {
  return {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors
  };
}

// 7. LocalBusiness
export function createLocalBusinessSchema({
  name = "Inchpaper Self Printing Kiosk & Document Services",
  description = "24x7 self-service document printing, photocopy, spiral binding & online xerox kiosks across Gurgaon, Delhi NCR & university campuses.",
  url = "https://print.inchpaper.com/print-centers",
  telephone = "+91-7703960982",
  email = "info@inchpaper.com",
  address = createPostalAddress(),
  geo = createGeoCoordinates(),
  googleMapsUrl = "https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8",
  googleBusinessProfileUrl = "https://maps.app.goo.gl/CBLs5VYGaPLRsH3s8",
  areaServed = [
    { "@type": "City", "name": "Gurgaon", "sameAs": "https://en.wikipedia.org/wiki/Gurgaon" },
    { "@type": "City", "name": "Sohna", "sameAs": "https://en.wikipedia.org/wiki/Sohna" },
    { "@type": "AdministrativeArea", "name": "Delhi NCR", "sameAs": "https://en.wikipedia.org/wiki/National_Capital_Region_(India)" },
    { "@type": "City", "name": "Faridabad" },
    { "@type": "City", "name": "Noida" },
    { "@type": "City", "name": "Delhi" },
    { "@type": "Place", "name": "Sector 48, Gurgaon" },
    { "@type": "Place", "name": "GD Goenka University Campus" },
    { "@type": "Place", "name": "KR Mangalam University Campus" }
  ],
  reviews = [
    createReview({ authorName: "Aarav Sharma", ratingValue: 5, reviewBody: "Super convenient kiosk at GD Goenka campus! Uploaded PDF from phone and printed in 30 seconds.", datePublished: "2026-01-14" }),
    createReview({ authorName: "Priya Mehta", ratingValue: 5, reviewBody: "High quality color printing and spiral binding for my thesis report. Extremely affordable rates.", datePublished: "2026-02-02" })
  ]
} = {}) {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "PrintingService"],
    "@id": url,
    "name": name,
    "description": description,
    "url": url,
    "telephone": telephone,
    "email": email,
    "priceRange": "₹",
    "currenciesAccepted": "INR",
    "paymentAccepted": "Cash, UPI, Credit Card, Debit Card, Net Banking",
    "hasMap": googleMapsUrl,
    "sameAs": [
      googleBusinessProfileUrl,
      "https://www.facebook.com/inchpaper",
      "https://www.instagram.com/inchpaper",
      "https://www.linkedin.com/company/inchpaper"
    ],
    "image": createImageObject({ url: "https://print.inchpaper.com/favicon-512.png" }),
    "address": address,
    "geo": geo,
    "areaServed": areaServed,
    "aggregateRating": createAggregateRating({ ratingValue: "4.9", reviewCount: "1280" }),
    "review": reviews,
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Document Printing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "A4 Black & White Printing"
          },
          "price": "2.00",
          "priceCurrency": "INR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "A4 Color Printing"
          },
          "price": "10.00",
          "priceCurrency": "INR"
        }
      ]
    }
  };
}

// 8. Service
export function createServiceSchema({
  name = "Online Document Printing Service",
  description = "Upload PDF or Word files online, customize paper GSM, color/BW settings, spiral binding, and print instantly at Inchpaper kiosks.",
  url = "https://print.inchpaper.com/print-pdf-online",
  price = "2.00",
  serviceType = "Document Printing"
} = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": url,
    name,
    serviceType,
    description,
    url,
    provider: {
      "@type": "LocalBusiness",
      name: "Inchpaper Private Limited",
      url: "https://print.inchpaper.com"
    },
    areaServed: {
      "@type": "Country",
      name: "India"
    },
    offers: {
      "@type": "Offer",
      price,
      priceCurrency: "INR",
      priceValidUntil: "2027-12-31",
      availability: "https://schema.org/InStock",
      url
    }
  };
}

// 9. FAQPage
export function createFAQPageSchema(faqItems = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(item => ({
      "@type": "Question",
      name: item.q || item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a || item.answer
      }
    }))
  };
}

// 10. BreadcrumbList
export function createBreadcrumbListSchema(breadcrumbs = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: b.name,
      item: b.url.startsWith('http') ? b.url : `https://print.inchpaper.com${b.url}`
    }))
  };
}

// 11. ContactPage
export function createContactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Inchpaper Print",
    description: "Connect with Inchpaper customer support, bulk printing desk, or request a campus kiosk installation.",
    url: "https://print.inchpaper.com/contact-us",
    mainEntity: {
      "@type": "ContactPoint",
      telephone: "+91-7703960982",
      email: "info@inchpaper.com",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"]
    }
  };
}

// 12. CollectionPage
export function createCollectionPageSchema({
  name = "Online Printing Services Catalog",
  description = "Browse high quality online document printing, spiral binding, xerox, and photo printing services.",
  url = "https://print.inchpaper.com/printing-services",
  hasPart = []
} = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
    hasPart: hasPart.map(item => ({
      "@type": "WebPage",
      name: item.title || item.name,
      url: item.url.startsWith('http') ? item.url : `https://print.inchpaper.com${item.url}`
    }))
  };
}

// 13. BlogPosting & Article
export function createBlogPostingSchema({
  title,
  excerpt,
  slug,
  image = "https://print.inchpaper.com/favicon-512.png",
  author = "Inchpaper Printing Experts",
  datePublished = "2026-01-15",
  dateModified = "2026-07-26",
  content = ""
}) {
  const pageUrl = `https://print.inchpaper.com/blog/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": ["BlogPosting", "Article"],
    "@id": pageUrl,
    headline: title,
    description: excerpt,
    articleBody: content || excerpt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl
    },
    image: createImageObject({ url: image, caption: title }),
    author: {
      "@type": "Person",
      name: author
    },
    publisher: {
      "@type": "Organization",
      name: "Inchpaper Private Limited",
      logo: createImageObject({ url: "https://print.inchpaper.com/inchpaper-logo.png" })
    },
    datePublished,
    dateModified,
    speakable: createSpeakable(["h1", ".blog-article-content", "p"])
  };
}

// 14. SiteNavigationElement
export function createSiteNavigationElementSchema() {
  const navItems = [
    { name: "Home", url: "https://print.inchpaper.com/" },
    { name: "Printing Services", url: "https://print.inchpaper.com/printing-services" },
    { name: "Pricing", url: "https://print.inchpaper.com/printing-prices" },
    { name: "Print Centers & Kiosks", url: "https://print.inchpaper.com/print-centers" },
    { name: "FAQ", url: "https://print.inchpaper.com/faq" },
    { name: "Contact Us", url: "https://print.inchpaper.com/contact-us" },
    { name: "Blog", url: "https://print.inchpaper.com/blog" }
  ];

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: navItems.map((item, index) => ({
      "@type": "SiteNavigationElement",
      position: index + 1,
      name: item.name,
      url: item.url
    }))
  };
}
